import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSubscription } from "@/hooks/useSubscription";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import SubscriptionBanner from "@/components/SubscriptionBanner";
import FeatureRouteGuard from "@/components/FeatureRouteGuard";

/**
 * AdminRouteGuard — parent-level gate for all /admin/* routes.
 *
 * MUST wrap the route element so the underlying page component (AdminProducts,
 * AdminDashboard, etc.) NEVER mounts — and therefore its useEffect fetches never
 * fire — until we are certain the tenant subscription is ACTIVE/TRIAL.
 *
 * Rendering matrix:
 *   authLoading || (isAdmin && subLoading)   -> Loading placeholder (no children)
 *   !isAdmin                                 -> Access Denied screen (no children)
 *   subscription in {SUSPENDED, EXPIRED}     -> Locked screen (no children)
 *   otherwise                                -> render children (route page mounts)
 */
export default function AdminRouteGuard({ children, featureKey }) {
  const { user, loading: authLoading, logout } = useAuth();
  const { subscription, loading: subLoading } = useSubscription();
  const navigate = useNavigate();

  const isAdmin = !!user && ["admin", "owner"].includes(user.role);

  // 1) Loading gate — waits for auth AND (for admins) subscription resolution.
  if (authLoading || (isAdmin && subLoading)) {
    return (
      <div
        className="min-h-screen grid place-items-center p-4"
        style={{ backgroundColor: "var(--brand-background)" }}
        data-testid="admin-subscription-loading"
      >
        <div className="text-sm text-[#5C5C5C] font-semibold animate-pulse">Memuat dashboard…</div>
      </div>
    );
  }

  // 2) Access denied — user is not an admin/owner.
  if (!isAdmin) {
    return (
      <div className="min-h-screen grid place-items-center p-4" style={{ backgroundColor: "var(--brand-background)" }}>
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-[#E5E5E5] text-center max-w-md">
          <h2 className="font-display text-2xl font-bold mb-2">Akses Ditolak</h2>
          <p className="text-[#5C5C5C] mb-4">Anda harus login sebagai admin untuk mengakses halaman ini.</p>
          <Link to="/login"><Button className="brand-btn-primary rounded-xl">Login Admin</Button></Link>
        </div>
      </div>
    );
  }

  // 3) Subscription lock — SUSPENDED or EXPIRED. Only banner + notice + logout.
  //    Children (route page components) are NOT rendered, so their useEffect fetches never fire.
  const subStatus = subscription?.status;
  const isLocked = subStatus === "SUSPENDED" || subStatus === "EXPIRED";
  if (isLocked) {
    return (
      <div className="min-h-screen flex flex-col" style={{ backgroundColor: "var(--brand-background)" }} data-testid="admin-subscription-locked">
        <header className="bg-white border-b border-[#E5E5E5] px-4 sm:px-6 py-3 flex items-center justify-between sticky top-0 z-30">
          <Logo variant="admin" size="sm" to="/admin" />
          <div className="flex items-center gap-3">
            <div className="hidden sm:block text-right">
              <div className="text-xs text-[#5C5C5C]">Login sebagai</div>
              <div className="text-sm font-semibold truncate max-w-[200px]">{user.email}</div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => { await logout(); navigate("/"); }}
              data-testid="admin-locked-logout-btn"
            >
              <LogOut size={14} /> Keluar
            </Button>
          </div>
        </header>
        <main className="flex-1 flex items-center justify-center px-4 py-10">
          <div className="w-full max-w-2xl">
            <SubscriptionBanner />
            <div
              className="bg-white rounded-2xl p-6 sm:p-8 border border-[#E5E5E5] shadow-sm text-center"
              data-testid="admin-subscription-lock-notice"
            >
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#1A1A1A] mb-2">
                {subStatus === "EXPIRED" ? "Langganan Berakhir" : "Langganan Ditangguhkan"}
              </h2>
              <p className="text-[#5C5C5C] text-sm sm:text-base leading-relaxed">
                {subStatus === "EXPIRED"
                  ? "Masa aktif langganan tenant Anda telah berakhir. Semua fitur admin dinonaktifkan sementara untuk melindungi data toko. Silakan perbarui langganan atau hubungi Platform Owner."
                  : "Langganan tenant Anda sedang ditangguhkan oleh Platform Owner. Semua fitur admin dinonaktifkan sementara. Silakan hubungi Platform Owner untuk mengaktifkan kembali."}
              </p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // 4) Feature gate — blokir direct URL kalau feature OFF untuk paket tenant.
  if (featureKey) {
    return <FeatureRouteGuard featureKey={featureKey}>{children}</FeatureRouteGuard>;
  }

  // 5) All good — render the actual admin page.
  return children;
}
